import React, { createContext, useContext, ReactNode, useCallback } from 'react';
import { useToast } from '@/hooks/use-toast';
import { useTranslation } from 'react-i18next';

interface SnackbarContextType {
    showSnackbar: (message: string, severity?: 'success' | 'error' | 'warning' | 'info') => void;
    dismissSnackbar: () => void;
}

const SnackbarContext = createContext<SnackbarContextType | undefined>(undefined);

export const useSnackbar = () => {
    const context = useContext(SnackbarContext);
    if (!context) {
        throw new Error('useSnackbar must be used within a SnackbarProvider');
    }
    return context;
};

interface SnackbarProviderProps {
    children: ReactNode;
}

export const SnackbarProvider: React.FC<SnackbarProviderProps> = ({ children }) => {
    const { toast, dismiss } = useToast();
    const { t } = useTranslation();

    const showSnackbar = useCallback((message: string, severity: 'success' | 'error' | 'warning' | 'info' = 'info') => {
        const toastConfig = {
            description: message,
            duration: 6000,
        };

        switch (severity) {
            case 'success':
                toast({ ...toastConfig, title: t('common.success') });
                break;
            case 'error':
                toast({ ...toastConfig, title: t('errors.error'), variant: 'destructive' });
                break;
            case 'warning':
                toast({ ...toastConfig, title: t('common.warning') });
                break;
            case 'info':
            default:
                toast({ ...toastConfig, title: t('common.info') });
                break;
        }
    }, [toast, t]);

    const dismissSnackbar = useCallback(() => {
        dismiss();
    }, [dismiss]);

    return (
        <SnackbarContext.Provider value={{ showSnackbar, dismissSnackbar }}>
            {children}
        </SnackbarContext.Provider>
    );
};
